/**
 * What is driving the month: the commentary Finance's sales tracker email used
 * to carry, built from rules rather than written.
 *
 * Everything here is in the `commentary` dataset and nothing is computed on the
 * page. The bridge runs from the plan to the agent's own projection and ties to
 * it exactly, so every figure a sentence quotes is one of its steps. The month-end
 * email renders the same rows, so the page and the email cannot disagree.
 *
 * The customers are drawn in the "All reps: this month" format of Sales Rep
 * Performance (owner, 2026-10-18): shipped, booked and still expected as one
 * bar, the plan as a tick. The bridge itself stays in the dataset and in the
 * month-end email, where it ties the text to the agent's total.
 */

import { Fragment, useState } from 'react'

import { dataset, type Keyed, text } from '../shared/rows'
import type { ReportResult } from '../../lib/types'

import { gallons, money, num, signedGallons, signedMoney, type Unit } from './format'
import { ProgressBullets } from '../rep-performance/charts'

type Business = 'SO' | 'NB' | 'ALL'

// The company view first, and it is the one that matters: Sun Orchard customer
// by customer, Natural Brands as one line -- "more how we think of it" (owner,
// 2026-10-18). The two single-business views stay a click away.
const BUSINESSES: readonly { key: Business; label: string }[] = [
  { key: 'ALL', label: 'Company' },
  { key: 'SO', label: 'Sun Orchard only' },
  { key: 'NB', label: 'Natural Brands detail' },
]

/** The tone a ship-to's status reads in. */
const STATUS_TONE: Record<string, string> = {
  'Not ordering yet': 'bad',
  'Running light': 'bad',
  'Running heavy': 'good',
  Unplanned: 'good',
  'On plan': 'neutral',
}

function signed(value: number | null, unit: Unit): string {
  return unit === 'usd' ? signedMoney(value) : `${signedGallons(value)} gal`
}

/** The email's name for A3's `zero_pred_gallons` (mailouts/month_end.py LUMP_LABEL). */
const LUMP_LABEL = 'Expected from accounts not due this month'

/**
 * The month by customer in the "All reps: this month" format (owner,
 * 2026-10-18): shipped, booked and still expected, the plan as a tick. From
 * the `chart` rows -- the largest Sun Orchard customers, then Natural Brands as
 * one line -- so it carries the commentary's own figures.
 */
function CustomerProgress({
  rows,
  unit,
  lumps,
}: {
  rows: readonly Keyed[]
  unit: Unit
  /** Each business's expected-but-not-due figure, keyed SO / NB. */
  lumps: Readonly<Record<string, number>>
}) {
  const shaped = rows.map((row): Keyed => {
    const shipped = num(row.shipped) ?? 0
    const known = num(row.known) ?? 0
    return {
      step: text(row.step),
      end_user: text(row.label),
      shipped,
      booked: Math.max(0, known - shipped),
      projected: num(row.scheduled) ?? 0,
      month_end: num(row.projected) ?? 0,
      budget: num(row.plan) ?? 0,
    }
  })
  const fmt = (value: number | null) => (unit === 'usd' ? money(value) : gallons(value))
  // Sun Orchard's lump is on none of its customers, so it is a row of its own
  // and counts in the total; Natural Brands' is already inside its one line,
  // so it is shown beneath it as "of which" and never added twice.
  const soLump = lumps.SO ?? 0
  const lumpRow: Keyed | null =
    Math.abs(soLump) >= 0.5
      ? {
          step: 'lump',
          end_user: `Sun Orchard: ${LUMP_LABEL.toLowerCase()}`,
          shipped: 0,
          booked: 0,
          projected: soLump,
          month_end: soLump,
          budget: 0,
        }
      : null
  const customerRows = shaped.filter((row) => row.step !== 'business')
  const businessRows = shaped.filter((row) => row.step === 'business')
  const counted = [...customerRows, ...(lumpRow === null ? [] : [lumpRow]), ...businessRows]
  const total = (key: string) => counted.reduce((sum, row) => sum + (num(row[key]) ?? 0), 0)
  const line = (row: Keyed) => {
    const end = num(row.month_end) ?? 0
    const plan = num(row.budget) ?? 0
    const isLump = row.step === 'lump'
    return (
      <tr key={text(row.end_user) ?? ''} className={isLump ? 'mef-cm-lump' : undefined}>
        <td>{text(row.end_user)}</td>
        <td className="cell-number">{isLump ? '' : fmt(num(row.shipped))}</td>
        <td className="cell-number">{isLump ? '' : fmt(num(row.booked))}</td>
        <td className="cell-number">{fmt(num(row.projected))}</td>
        <td className="cell-number srp-strong">{fmt(end)}</td>
        <td className="cell-number">{isLump ? '' : fmt(plan)}</td>
        <td className={`cell-number ${end - plan < 0 ? 'mef-down' : 'mef-up'}`}>
          {signed(end - plan, unit)}
        </td>
        <td className="cell-number">{plan > 0 ? `${String(Math.round((end / plan) * 100))}%` : ''}</td>
      </tr>
    )
  }
  return (
    <>
      <ProgressBullets
        rows={shaped}
        unit={unit === 'usd' ? 'fob' : 'gal'}
        title="Largest customers this month"
        label="Largest customers this month against plan"
        keyColumn="end_user"
        planName="plan"
      />
      <div className="srp-scroll">
        <table className="srp-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th className="cell-number">Shipped</th>
              <th className="cell-number">Booked</th>
              <th className="cell-number">Still expected</th>
              <th className="cell-number">Projected</th>
              <th className="cell-number">Plan</th>
              <th className="cell-number">vs plan</th>
              <th className="cell-number">% of plan</th>
            </tr>
          </thead>
          <tbody>
            {customerRows.map(line)}
            {lumpRow !== null && line(lumpRow)}
            {businessRows.map((row) => {
              const nb = (text(row.end_user) ?? '').startsWith('Natural Brands') ? (lumps.NB ?? 0) : 0
              return (
                <Fragment key={text(row.end_user) ?? ''}>
                  {line(row)}
                  {Math.abs(nb) >= 0.5 && (
                    <tr className="mef-cm-ofwhich">
                      <td>of which {LUMP_LABEL.toLowerCase()}</td>
                      <td />
                      <td />
                      <td className="cell-number">{fmt(nb)}</td>
                      <td colSpan={4} />
                    </tr>
                  )}
                </Fragment>
              )
            })}
            <tr className="srp-total">
              <td>Total shown</td>
              <td className="cell-number">{fmt(total('shipped'))}</td>
              <td className="cell-number">{fmt(total('booked'))}</td>
              <td className="cell-number">{fmt(total('projected'))}</td>
              <td className="cell-number srp-strong">{fmt(total('month_end'))}</td>
              <td className="cell-number">{fmt(total('budget'))}</td>
              <td className="cell-number">{signed(total('month_end') - total('budget'), unit)}</td>
              <td className="cell-number">
                {total('budget') > 0
                  ? `${String(Math.round((total('month_end') / total('budget')) * 100))}%`
                  : ''}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  )
}

export function DrivingTheMonth({ result, unit }: { result: ReportResult; unit: Unit }) {
  const [business, setBusiness] = useState<Business>('ALL')
  const rows = dataset(result, 'commentary').filter(
    (row) => row.business === business && row.measure === unit,
  )
  if (dataset(result, 'commentary').length === 0) return null

  const of = (kind: string) =>
    rows.filter((row) => row.kind === kind).sort((a, b) => (num(a.seq) ?? 0) - (num(b.seq) ?? 0))
  const headlines = of('headline')
  const chart = of('chart')
  const drivers = of('driver')
  const evidence = of('evidence')
  // Each business's own bridge carries its `lump` step; the table shows them.
  const lumps: Record<string, number> = {}
  for (const row of dataset(result, 'commentary')) {
    if (row.kind === 'bridge' && row.step === 'lump' && row.measure === unit) {
      const key = text(row.business)
      if (key === 'SO' || key === 'NB') lumps[key] = num(row.value) ?? 0
    }
  }

  return (
    <section className="card mef-cm" aria-labelledby="mef-cm-title">
      <header className="card-header">
        <div>
          <h2 className="card-title" id="mef-cm-title">
            What is driving the month
          </h2>
          <p className="card-subtitle">
            The largest Sun Orchard customers and Natural Brands as one line: shipped, booked and what the
            agent still expects, against the plan (the dark tick). Then what moved the month, customer by
            customer.
          </p>
        </div>
        <div className="mef-cm-switch" role="group" aria-label="Business">
          {BUSINESSES.map((b) => (
            <button
              key={b.key}
              type="button"
              aria-pressed={business === b.key}
              className={business === b.key ? 'is-on' : undefined}
              onClick={() => {
                setBusiness(b.key)
              }}
            >
              {b.label}
            </button>
          ))}
        </div>
      </header>

      <div className="mef-cm-text">
        {headlines.map((row) => (
          <p key={String(row.seq)}>{text(row.text)}</p>
        ))}
      </div>

      {chart.length > 0 && <CustomerProgress rows={chart} unit={unit} lumps={lumps} />}

      {drivers.length > 0 && (
        <ul className="mef-cm-drivers">
          {drivers.map((row) => {
            const name = text(row.end_user) ?? ''
            const status = text(row.status) ?? ''
            const value = num(row.value)
            return (
              <li key={name} className="mef-cm-driver">
                <div className="mef-cm-driver-head">
                  <span className="mef-cm-driver-name">{name}</span>
                  <span className={(value ?? 0) < 0 ? 'mef-down' : 'mef-up'}>{signed(value, unit)}</span>
                  <span className={`mef-cm-chip is-${STATUS_TONE[status] ?? 'neutral'}`}>{status}</span>
                </div>
                <p className="mef-cm-driver-text">{text(row.text)}</p>
                <ul className="mef-cm-evidence">
                  {evidence
                    .filter((e) => e.end_user === name)
                    .map((e) => (
                      <li key={`${name}-${text(e.ship_to) ?? ''}`}>{text(e.text)}</li>
                    ))}
                </ul>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
